import React, { useState, useContext, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import DotMovement from "./DotMovement";
import MoodTracker from "./MoodTracker";
import ReadinessCheck from "./ReadinessCheck";
import CrisisModal from "./CrisisModal";
import DotContext from "../contexts/DotContext";
import { SessionThemeWrapper } from "../contexts/SessionThemeContext";
import DynamicTitle from "./DynamicTitle";
import { trackEvent } from "../utils/posthog";
import "./styles/GuidedSession.css";

// Gentle, non-clinical encouragement that rotates during the dot movement so a
// first-timer always has a hand to hold. Keep the vocabulary calm/settle — no
// clinical or trauma terms.
const ENCOURAGEMENT_CUES = [
  "Just follow the dot",
  "You're doing great",
  "Let your eyes do the work",
  "Settle into the rhythm",
  "No wrong way to do this",
  "Let your shoulders drop",
];

const CUE_INTERVAL_MS = 9000;

/** Rotating encouragement pill shown over the moving dot. */
function EncouragementCue({ paused }) {
  const [index, setIndex] = useState(0);
  useEffect(() => {
    if (paused) return undefined;
    const id = setInterval(
      () => setIndex((i) => (i + 1) % ENCOURAGEMENT_CUES.length),
      CUE_INTERVAL_MS
    );
    return () => clearInterval(id);
  }, [paused]);

  return (
    <div className="session-cue-overlay absolute inset-0 z-10 flex items-end justify-center pb-24 pointer-events-none">
      <span
        key={index}
        className="session-cue-text px-4 py-2 rounded-full text-xl md:text-2xl font-normal select-none"
      >
        {ENCOURAGEMENT_CUES[index]}
      </span>
    </div>
  );
}

/**
 * A full, unscripted bilateral-stimulation session that anyone can run with no
 * account. Makes ZERO API calls — mood is held locally and shown as motivation
 * on the way out, then discarded. Converts on the completion screen.
 */
function PublicTrySession() {
  const navigate = useNavigate();
  const {
    bgColor,
    dotColor,
    dotGlow,
    dotSize,
    dotSpeed,
    dotBrightness,
    isPaused,
    setIsPaused,
    setSidebarVisible,
    sidebarVisible,
    soundFile,
    mute,
    volume,
    exitSession,
    setExitSession,
  } = useContext(DotContext);

  // Flow: readiness → preMood → session → (continue?) → postMood → complete
  const [stage, setStage] = useState("readiness");
  const [preMood, setPreMood] = useState(null);
  const [postMood, setPostMood] = useState(null);
  const [showContinuePrompt, setShowContinuePrompt] = useState(false);
  const [showCrisis, setShowCrisis] = useState(false);
  const startedAtRef = useRef(null);

  useEffect(() => {
    if (stage === "complete") {
      trackEvent("full_session_upsell_shown", {
        mood_delta: preMood !== null && postMood !== null ? postMood - preMood : null,
      });
    }
    // Only re-run when the stage changes; mood values are settled by then.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage]);

  // Sidebar "exit" (global) ends the session and routes to the post-mood check.
  useEffect(() => {
    if (exitSession) {
      setExitSession(false);
      setIsPaused(false);
      setShowContinuePrompt(false);
      setStage("postMood");
    }
  }, [exitSession, setExitSession, setIsPaused]);

  const beginSession = () => {
    startedAtRef.current = Date.now();
    setStage("session");
    trackEvent("full_session_started", {
      source: "try_page",
      mood_pre: preMood,
      is_authenticated: false,
    });
  };

  const handlePreMood = (mood) => {
    setPreMood(mood);
    if (mood <= 3) {
      setShowCrisis(true);
      return;
    }
    beginSession();
  };

  const handleCrisisChoice = (continueAnyway) => {
    setShowCrisis(false);
    if (continueAnyway) {
      beginSession();
    } else {
      navigate("/emergency-support");
    }
  };

  const endDotMovement = () => {
    // One round of 42 cycles finished — offer to keep going, self-paced.
    setShowContinuePrompt(true);
  };

  const handleContinueChoice = (keepGoing) => {
    setShowContinuePrompt(false);
    if (keepGoing) {
      setStage("session");
    } else {
      setStage("postMood");
    }
  };

  const handlePostMood = (mood) => {
    setPostMood(mood);
    setSidebarVisible(false);
    const durationSeconds = startedAtRef.current
      ? Math.round((Date.now() - startedAtRef.current) / 1000)
      : null;
    trackEvent("full_session_completed", {
      source: "try_page",
      mood_pre: preMood,
      mood_post: mood,
      mood_delta: preMood !== null ? mood - preMood : null,
      duration_seconds: durationSeconds,
      is_authenticated: false,
    });
    setStage("complete");
  };

  const goCreateAccount = () => {
    trackEvent("full_session_upsell_clicked", { cta: "create_free_account" });
    navigate("/login", { state: { mode: "register", from: "/try" } });
  };

  const goPremium = () => {
    trackEvent("full_session_upsell_clicked", { cta: "premium" });
    navigate("/subscribe", { state: { from: "/try" } });
  };

  const togglePause = () => {
    if (!isPaused) trackEvent("session_paused", { session_type: "PublicTry" });
    setIsPaused(!isPaused);
  };

  const showDot = stage === "session";
  const moodDelta = preMood !== null && postMood !== null ? postMood - preMood : null;

  return (
    <SessionThemeWrapper className="relative flex flex-col items-center justify-center h-screen bg-session-bg text-session-text">
      <DynamicTitle>Try a Full Session</DynamicTitle>

      {/* Settings hamburger (Sidebar is mounted globally) — hidden behind modals. */}
      {showDot && !sidebarVisible && !showContinuePrompt && (
        <button
          onClick={() => setSidebarVisible(!sidebarVisible)}
          className="absolute top-4 landscape:top-1 left-4 landscape:left-2 text-xl landscape:text-lg z-50 focus:outline-none"
          aria-label="Settings"
        >
          <span className="hamburger" style={{ color: dotColor }}>
            &#9776;
          </span>
        </button>
      )}

      {stage === "readiness" && (
        <ReadinessCheck
          onContinue={() => setStage("preMood")}
          onCancel={() => navigate("/")}
        />
      )}

      {stage === "preMood" && (
        <MoodTracker onSubmit={handlePreMood} isPreSession={true} />
      )}

      {showCrisis && (
        <CrisisModal
          handleRejectWarning={handleCrisisChoice}
          isInCrisis={preMood === 1}
        />
      )}

      {showDot && (
        <div className="w-full h-full flex items-center justify-center relative">
          <EncouragementCue paused={isPaused} />
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-3 z-50">
            <button
              onClick={togglePause}
              className="bg-session-accent text-session-bg py-2 landscape:py-1 px-6 landscape:px-4 rounded hover:opacity-90 transition font-bold landscape:text-sm pause-button-safe"
            >
              {isPaused ? "Resume" : "Pause"}
            </button>
            <button
              onClick={() => setStage("postMood")}
              className="bg-session-bg text-session-text border border-session-text-secondary/30 py-2 landscape:py-1 px-6 landscape:px-4 rounded hover:bg-session-bg/70 transition font-semibold landscape:text-sm"
            >
              End
            </button>
          </div>
          <DotMovement
            shouldRun={showDot}
            isPaused={isPaused}
            bgColor={bgColor}
            dotColor={dotColor}
            dotGlow={dotGlow}
            dotBrightness={dotBrightness}
            dotSize={dotSize}
            dotSpeed={dotSpeed}
            onCompletion={endDotMovement}
            soundFile={soundFile}
            mute={mute}
            volume={volume}
          />
        </div>
      )}

      {showContinuePrompt && (
        <div className="fixed inset-0 z-40 bg-session-bg sm:bg-black/50 flex items-stretch sm:items-center justify-center sm:p-4">
          <div className="w-full sm:max-w-md bg-session-bg-elevated text-session-text sm:rounded-2xl sm:shadow-xl flex flex-col">
            <div className="flex-1 px-6 pt-10 pb-6 sm:py-8">
              <p className="text-lg text-session-text leading-relaxed text-center">
                Would you like to keep going?
              </p>
            </div>
            <div className="px-6 pb-6 sm:pb-8 pt-4 flex flex-col gap-2">
              <button
                onClick={() => handleContinueChoice(true)}
                className="w-full bg-session-accent text-session-bg font-semibold py-4 rounded-xl hover:opacity-90 transition"
              >
                Keep going
              </button>
              <button
                onClick={() => handleContinueChoice(false)}
                className="w-full bg-session-bg text-session-text border border-session-text-secondary/20 font-semibold py-4 rounded-xl hover:bg-session-bg/70 transition"
              >
                I'm done
              </button>
            </div>
          </div>
        </div>
      )}

      {stage === "postMood" && (
        <MoodTracker onSubmit={handlePostMood} isPreSession={false} />
      )}

      {stage === "complete" && (
        <div className="fixed inset-0 z-40 bg-session-bg text-session-text overflow-y-auto flex items-center justify-center p-6">
          <div className="w-full max-w-md text-center">
            <div className="text-5xl mb-4" role="img" aria-label="calm">✨</div>
            <h1 className="text-3xl font-semibold mb-2">Nicely done.</h1>
            {moodDelta !== null && moodDelta > 0 ? (
              <p className="text-session-text-secondary mb-8">
                You went from <strong className="text-session-text">{preMood}</strong> to{" "}
                <strong className="text-session-text">{postMood}</strong> out of 10.
                Create a free account to save moments like this and build a streak.
              </p>
            ) : (
              <p className="text-session-text-secondary mb-8">
                That's a full session. Create a free account to save your sessions and build a streak.
              </p>
            )}

            <div className="flex flex-col gap-3">
              <button
                onClick={goCreateAccount}
                className="w-full bg-session-accent text-session-bg font-semibold py-4 rounded-xl hover:opacity-90 transition"
              >
                Create a free account
              </button>
              <button
                onClick={goPremium}
                className="w-full bg-session-bg text-session-text border border-session-text-secondary/20 font-semibold py-4 rounded-xl hover:bg-session-bg/70 transition"
              >
                See what's included
              </button>
              <button
                onClick={() => window.location.reload()}
                className="text-sm text-session-text-secondary hover:text-session-text transition mt-2"
              >
                Try another session
              </button>
            </div>

            <div className="text-xs text-session-text-secondary/70 mt-10 space-y-1">
              <p>A wellness tool — not therapy. It can't diagnose, treat, or cure any condition.</p>
              <p>Need to talk to someone? Call or text 988.</p>
            </div>
          </div>
        </div>
      )}
    </SessionThemeWrapper>
  );
}

export default PublicTrySession;
